import React from 'react';
import type { LanguageOption } from '@ditioas/web-ui';
import { LanguageFlag } from '@ditioas/web-ui';

import { LocalStorageManager } from 'libs/utils';

export const defaultLocale = 'nb';

// Every language the app can render in
export const locales = {
  EN: 'en',
  NB: defaultLocale,
  PL: 'pl',
  SV: 'sv',
} as const;

export type LocaleId = (typeof locales)[keyof typeof locales];

// Languages the selector never offers on its own. Polish is hidden while its
// translations are incomplete — users already on `pl` still get a Polish UI,
// and keep `pl` in their selector (see getLocaleOptions).
const hiddenLocales: LocaleId[] = [locales.PL];

// One option per supported language, selectable or not
const localeOptions: LanguageOption[] = [
  {
    id: locales.NB,
    flagIcon: <LanguageFlag lang="nb" />,
    name: 'Norwegian',
    shortName: 'Nor',
  },
  {
    id: locales.EN,
    flagIcon: <LanguageFlag lang="en" />,
    name: 'English',
    shortName: 'Eng',
  },
  {
    id: locales.SV,
    flagIcon: <LanguageFlag lang="sv" />,
    name: 'Swedish',
    shortName: 'Swe',
  },
  {
    id: locales.PL,
    flagIcon: <LanguageFlag lang="pl" />,
    name: 'Polish',
    shortName: 'Pol',
  },
];

// Languages offered in the language selector
export const localeList: LanguageOption[] = localeOptions.filter(
  (option) => !hiddenLocales.includes(option.id as LocaleId)
);

const defaultLocalObject = localeOptions.find(
  (item) => item.id === defaultLocale
)!;

export const findLocaleById = (id: LocaleId = defaultLocale) => {
  if (!id) {
    return defaultLocalObject;
  }

  const foundLocale = localeOptions.find(
    (language) => language.id === id.toLowerCase()
  );

  if (!foundLocale) {
    return defaultLocalObject;
  }

  return foundLocale;
};

/**
 * Options for the language selector: the selectable languages, plus the active
 * one when the app runs in a language that is otherwise hidden — so a Polish
 * user sees "Polish" selected instead of a wrong label.
 */
export const getLocaleOptions = (activeId?: LocaleId | null) => {
  const activeHidden = localeOptions.find(
    (option) =>
      option.id === activeId?.toLowerCase() &&
      hiddenLocales.includes(option.id as LocaleId)
  );

  if (!activeHidden) {
    return localeList;
  }

  return [...localeList, activeHidden];
};

// Checks against every supported language, not just the selectable ones
export const localeExists = (id?: string | null): id is LocaleId =>
  !!id && (Object.values(locales) as string[]).includes(id.toLowerCase());

// Browser language tags that map onto a supported locale under another name
const localeAliases: Record<string, LocaleId> = {
  nn: locales.NB,
  no: locales.NB,
};

/**
 * Best supported locale for the browser's language preferences.
 */
export const getBrowserLocale = (): LocaleId => {
  const preferred = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  const match = preferred
    .filter(Boolean)
    .map((language) => language.split('-')[0].toLowerCase())
    .map((language) => localeAliases[language] ?? language)
    .find(localeExists);

  return match ?? defaultLocale;
};

/**
 * Language to start the app in, before the signed-in user is known (login,
 * logged-out and callback screens, and the first render of every other page).
 * Prefers the language last used on this device — cached on every locale
 * change in appWrapper — so returning users don't see a flash of the wrong
 * language while the current user loads.
 */
export const getInitialLocale = (): LocaleId => {
  const cached = LocalStorageManager.getLanguageId()?.toLowerCase();

  if (localeExists(cached)) {
    return cached;
  }

  return getBrowserLocale();
};

export const cacheLocale = (id: LocaleId) => {
  LocalStorageManager.setLanguageId(id.toLowerCase());
};
